import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppTranslation } from "./TranslationContext";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Code from '@tiptap/extension-code';
import Blockquote from '@tiptap/extension-blockquote';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import s from '../Yakuniy.module.css';

const MAX_QUESTIONS = 5;
const API = 'https://api.rasulova-academy.uz/api';

export const Yakuniy = () => {
  const { user, userKursData, setUserKursData } = useAuth();
  const { kursId } = useParams();
  const { t } = useAppTranslation();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentStep, setCurrentStep] = useState(0);
  const [stepAnswers, setStepAnswers] = useState([]);
  const [charCount, setCharCount] = useState(0);
  const [editorEmpty, setEditorEmpty] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [hasAnswered, setHasAnswered] = useState(false);
  const [results, setResults] = useState([]);

  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [isLinkModalOpen, setLinkModalOpen] = useState(false);
  const [imageValue, setImageValue] = useState('');
  const [linkValue, setLinkValue] = useState('');

  const pollingRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit, Underline, Strike, Code, Blockquote, Image,
      Link.configure({ openOnClick: true }),
    ],
    content: '',
    onUpdate({ editor }) {
      setCharCount(editor.getText().length);
      setEditorEmpty(editor.isEmpty);
    },
  });

  useEffect(() => {
    if (!editor || !user?.id) return;
    const fetchData = async () => {
      try {
        const [questionsRes, answersRes] = await Promise.all([
          fetch(`${API}/kurs-yozma-savol/?kurs=${kursId}`),
          fetch(`${API}/yozma-javob/?kurs=${kursId}&user=${user.id}`),
        ]);
        const questionsData = await questionsRes.json();
        const answersData = await answersRes.json();

        const selected = Array.isArray(questionsData)
          ? questionsData.slice(0, MAX_QUESTIONS)
          : [];
        setQuestions(selected);
        setStepAnswers(new Array(selected.length).fill(''));

        if (answersData.length > 0) {
          setHasAnswered(true);
          setResults(answersData);
          editor.setOptions({ editable: false });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [editor, user]);

  // Poll until all answers are graded
  useEffect(() => {
    if (!hasAnswered) return;
    const allGraded = results.length > 0 && results.every(r => r.ball !== null);
    if (allGraded) return;

    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/yozma-javob/?kurs=${kursId}&user=${user.id}`);
        const data = await res.json();
        setResults(data);
        if (data.every(r => r.ball !== null)) clearInterval(pollingRef.current);
      } catch {}
    }, 15000);

    return () => clearInterval(pollingRef.current);
  }, [hasAnswered, results]);

  const goToStep = (newStep) => {
    const html = editor?.getHTML() || '';
    const updatedAnswers = [...stepAnswers];
    updatedAnswers[currentStep] = html;
    setStepAnswers(updatedAnswers);
    editor?.commands.setContent(updatedAnswers[newStep] || '');
    setCurrentStep(newStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    const html = editor?.getHTML() || '';
    const allAnswers = [...stepAnswers];
    allAnswers[currentStep] = html;

    setIsSubmitting(true);
    try {
      const submitted = await Promise.all(
        questions.map((q, i) =>
          fetch(`${API}/yozma-javob/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              javob: allAnswers[i] || '<p></p>',
              kurs: kursId,
              savol: q.id,
              user: user.id,
            }),
          }).then(r => r.json())
        )
      );

      setResults(submitted);
      setHasAnswered(true);
      setShowModal(true);
      editor?.setOptions({ editable: false });

      if (userKursData && parseInt(userKursData.kurs) === parseInt(kursId)) {
        try {
          const patchRes = await fetch(`${API}/user-kurs/${userKursData.id}/`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user: user.id,
              kurs: parseInt(kursId),
              mavzu: parseInt(userKursData.mavzu, 10) + 1,
            }),
          });
          if (patchRes.ok) {
            const updated = await patchRes.json();
            setUserKursData({ ...userKursData, mavzu: updated.mavzu });
          }
        } catch (err) {
          console.error(err);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const MenuBar = ({ editor }) => {
    if (!editor) return null;
    return (
      <div className={s.menuBar}>
        <button onClick={() => editor.chain().focus().toggleBold().run()}><strong>B</strong></button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></button>
        <button onClick={() => editor.chain().focus().toggleUnderline().run()}><u>U</u></button>
        <button onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></button>
        <button onClick={() => editor.chain().focus().toggleCode().run()}>{'</>'}</button>
        <button onClick={() => editor.chain().focus().toggleBlockquote().run()}>❝</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>H1</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</button>
        <button onClick={() => editor.chain().focus().toggleBulletList().run()}>• {t('list')}</button>
        <button onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. {t('list')}</button>
        <button onClick={() => editor.chain().focus().unsetAllMarks().run()}>{t('clear')}</button>
        <button onClick={() => setImageModalOpen(true)}>🖼️</button>
        <button onClick={() => setLinkModalOpen(true)}>🔗</button>
        <button onClick={() => editor.chain().focus().unsetLink().run()}>✕ link</button>
      </div>
    );
  };

  // ─── Results view ───────────────────────────────────────────────────────────
  if (!loading && hasAnswered) {
    const graded = results.filter(r => r.ball !== null);
    const allGraded = results.length > 0 && graded.length === results.length;
    const avgBall = graded.length > 0
      ? (graded.reduce((sum, r) => sum + r.ball, 0) / graded.length).toFixed(1)
      : null;

    return (
      <div className={s.container}>
        <h2 className={s.title}>{t('yakuniyTitle')}</h2>

        {/* Average score card */}
        <div className={s.resultSummary}>
          <div className={`${s.avgCircle} ${avgBall >= 70 ? s.avgHigh : avgBall >= 55 ? s.avgMid : avgBall !== null ? s.avgLow : s.avgPending}`}>
            <span className={s.avgNum}>{avgBall ?? '—'}</span>
            <span className={s.avgSub}>/ 100</span>
          </div>
          <div className={s.summaryText}>
            <strong>O'rtacha ball</strong>
            <p>{graded.length} / {results.length} ta savol baholandi</p>
            {!allGraded && (
              <div className={s.pendingSmall}>
                <div className={s.pendingSpinner} />
                Qolgan javoblar tekshirilmoqda, avtomatik yangilanadi...
              </div>
            )}
          </div>
        </div>

        {/* Per-question results */}
        <div className={s.resultsList}>
          {questions.map((q, i) => {
            const result = results.find(r => r.savol === q.id);
            const ball = result?.ball;
            const isGraded = ball !== null && ball !== undefined;

            return (
              <div key={q.id} className={`${s.resultCard} ${isGraded ? s.resultGraded : s.resultPending}`}>
                <div className={s.resultHeader}>
                  <span className={s.qNum}>{i + 1}</span>
                  <div className={s.qText} dangerouslySetInnerHTML={{ __html: q.savol_uz }} />
                  {isGraded && (
                    <span className={`${s.ballPill} ${ball >= 70 ? s.pillHigh : ball >= 55 ? s.pillMid : s.pillLow}`}>
                      {ball} / 100
                    </span>
                  )}
                </div>

                <div className={s.resultAnswer}>
                  <span className={s.boxLabel}>Sizning javobingiz</span>
                  <div dangerouslySetInnerHTML={{ __html: result?.javob || '<em>—</em>' }} />
                </div>

                {isGraded ? (
                  <div className={s.aiBox}>
                    <span className={s.boxLabel}>AI izohi</span>
                    <p className={s.aiText}>{result.komment}</p>
                  </div>
                ) : (
                  <div className={s.pendingSmall}>
                    <div className={s.pendingSpinner} />
                    Tekshirilmoqda...
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── Step form ───────────────────────────────────────────────────────────────
  const total = questions.length;
  const progress = total > 0 ? (currentStep / total) * 100 : 0;
  const isLastStep = currentStep === total - 1;
  const currentQuestion = questions[currentStep];
  const answeredCount = stepAnswers.filter(a => a && a !== '<p></p>' && a !== '').length;

  return (
    <div className={s.container}>
      <h2 className={s.title}>{t('yakuniyTitle')}</h2>

      {loading ? (
        <div className={s.loadingWrap}>
          <div className={s.loadingSpinner} />
          <p>Yuklanmoqda...</p>
        </div>
      ) : total === 0 ? (
        <div className={s.emptyQuestions}>
          <span>📭</span>
          <p>Bu kurs uchun yakuniy savollar hali qo'shilmagan.</p>
        </div>
      ) : (
        <>
          {/* Question count info banner */}
          {total < MAX_QUESTIONS && (
            <div className={s.infoBanner}>
              ℹ️ Ushbu kurs bo'yicha hozircha <strong>{total} ta</strong> yakuniy savol mavjud
              (maksimal {MAX_QUESTIONS} ta).
            </div>
          )}

          {/* Step progress */}
          <div className={s.stepProgress}>
            <div className={s.stepLabels}>
              <span className={s.stepCurrent}>Savol {currentStep + 1} / {total}</span>
              <span className={s.stepDone}>{answeredCount} ta yozildi</span>
            </div>
            <div className={s.stepBar}>
              <div className={s.stepFill} style={{ width: `${progress}%` }} />
            </div>
            {/* Step dots */}
            <div className={s.stepDots}>
              {questions.map((_, i) => {
                const answered = stepAnswers[i] && stepAnswers[i] !== '<p></p>';
                return (
                  <button
                    key={i}
                    className={`${s.stepDot} ${i === currentStep ? s.dotActive : answered ? s.dotDone : s.dotEmpty}`}
                    onClick={() => goToStep(i)}
                    title={`Savol ${i + 1}`}
                  >
                    {answered && i !== currentStep ? '✓' : i + 1}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Question */}
          <div className={s.question}>
            <span className={s.questionNum}>Savol {currentStep + 1}</span>
            <div dangerouslySetInnerHTML={{ __html: currentQuestion?.savol_uz }} />
          </div>

          {/* Hint */}
          <div className={s.hint}>
            💡 Javobingizni to'liq va batafsil yozing — misollar keltiring, tushunchalarni izohlang.
            Qanchalik mukammal javob, shunchalik yuqori ball.
          </div>

          <MenuBar editor={editor} />

          <div className={`${s.editorBox}${editorEmpty ? ` ${s.editorEmpty}` : ''}`}>
            <EditorContent editor={editor} />
          </div>

          <div className={s.editorFooter}>
            <span className={s.charCount}>{charCount} ta belgi</span>
            {charCount > 0 && charCount < 50 && (
              <span className={s.charWarn}>⚠️ Kamida 50 ta belgi yozing</span>
            )}
          </div>

          {/* Navigation */}
          <div className={s.navRow}>
            <button
              className={s.prevBtn}
              onClick={() => goToStep(currentStep - 1)}
              disabled={currentStep === 0}
            >
              ← Oldingi
            </button>

            <span className={s.navHint}>
              {isLastStep
                ? 'Barcha savollarga javob bergach, yuboring'
                : "Keyingi savolga o'tish uchun bosing"}
            </span>

            {isLastStep ? (
              <button
                className={s.submitButton}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? <><span className={s.spinner} /> Yuborilmoqda...</>
                  : `Yakunlash va yuborish (${total} ta javob)`}
              </button>
            ) : (
              <button className={s.nextBtn} onClick={() => goToStep(currentStep + 1)}>
                Keyingisi →
              </button>
            )}
          </div>
        </>
      )}

      {/* Image modal */}
      {isImageModalOpen && (
        <div className={s.modalOverlay}>
          <div className={s.modal}>
            <h3>{t('enterImg')}</h3>
            <input type="text" value={imageValue} onChange={e => setImageValue(e.target.value)} placeholder={t('enterUrlImg')} />
            <div className={s.modalActions}>
              <button onClick={() => { editor.chain().focus().setImage({ src: imageValue }).run(); setImageValue(''); setImageModalOpen(false); }}>{t('enterImg')}</button>
              <button onClick={() => setImageModalOpen(false)}>{t('cansel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Link modal */}
      {isLinkModalOpen && (
        <div className={s.modalOverlay}>
          <div className={s.modal}>
            <h3>{t('enterLink')}</h3>
            <input type="text" value={linkValue} onChange={e => setLinkValue(e.target.value)} placeholder={t('enterUrlLink')} />
            <div className={s.modalActions}>
              <button onClick={() => { editor.chain().focus().extendMarkRange('link').setLink({ href: linkValue }).run(); setLinkValue(''); setLinkModalOpen(false); }}>{t('add')}</button>
              <button onClick={() => setLinkModalOpen(false)}>{t('cansel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Success modal */}
      {showModal && (
        <div className={s.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={s.modal} onClick={e => e.stopPropagation()}>
            <div className={s.successIcon}>✓</div>
            <h3>Barcha javoblar qabul qilindi!</h3>
            <p>{total} ta savolga javob yuborildi va AI tekshiruviga yuborildi.</p>
            <p className={s.modalHint}>
              Natijalar tayyor bo'lgach, bu sahifada avtomatik ko'rinadi.
              Sahifani yopmasangiz ham bo'ladi.
            </p>
            <button className={s.submitButton} onClick={() => setShowModal(false)}>Natijalarni kutish</button>
          </div>
        </div>
      )}
    </div>
  );
};
